import api from './axiosInstance';

// Types
export interface ProjectSpecFile {
  fileDescription?: string;
  url?: string;
}

export interface ProjectSpec {
  specDescriptionText?: string;
  files: ProjectSpecFile[];
}

export interface Project {
  _id: string;
  userWhoCreatedProject: {
    _id: string;
    firstName: string;
    lastName: string;
    email: string;
  };
  projectNumber: string; // Unique project number: PP-ClientInitials-RandomNumber
  projectName: string;
  description?: string;
  type: string;
  client?:
    | {
        _id: string;
        clientName: string;
      }
    | string
    | null;
  tasks: string[];
  assignedToUser?: Array<{ _id: string; firstName: string; lastName: string; email: string }> | string[];
  spec?: ProjectSpec[]; // Project specification
  authorizedBy?: string[];
  quotedPrice?: number;
  agreedPrice?: number;
  toBeginOn?: string;
  toBeCompletedBy?: string;
  createdAt: string;
  updatedAt: string;
  archived?: boolean;
}

export interface CreateProjectData {
  userWhoCreatedProject: string;
  projectName: string;
  description?: string;
  type: string;
  client?: string;
  spec?: ProjectSpec[];
  authorizedBy?: string[];
  assignedToUser?: string[];
  quotedPrice?: number;
  agreedPrice?: number;
  toBeginOn?: string;
  toBeCompletedBy?: string;
}

export interface UpdateProjectData {
  projectName?: string;
  description?: string;
  type?: string;
  client?: string;
  tasks?: string[];
  spec?: ProjectSpec[];
  authorizedBy?: string[];
  assignedToUser?: string[];
  quotedPrice?: number;
  agreedPrice?: number;
  toBeginOn?: string;
  toBeCompletedBy?: string;
}

// API Functions
export const projectApi = {
  // PATCH /api/projects/:projectId/spec/:specIdx/add-file - Add a file to a spec entry
  addSpecFile: async (projectId: string, specIdx: number, url: string, fileDescription?: string): Promise<Project> => {
    const response = await api.patch(`/projects/${projectId}/spec/${specIdx}/add-file`, { url, fileDescription });
    return response.data;
  },
  // PATCH /api/projects/:projectId/spec/:specIdx/remove-file/:fileIdx - Remove a file from a spec entry
  removeSpecFile: async (projectId: string, specIdx: number, fileIdx: number): Promise<Project> => {
    const response = await api.patch(`/projects/${projectId}/spec/${specIdx}/remove-file/${fileIdx}`);
    return response.data;
  },
  // GET /api/projects - Get all projects (with optional showAll and includeArchived param)
  getAllProjects: async (showAll: boolean = false, includeArchived: boolean = true): Promise<Project[]> => {
    const params: any = {};
    if (showAll) params.showAll = true;
    if (includeArchived) params.includeArchived = true;
    const response = await api.get('/projects', { params });
    return response.data;
  },

  // GET /api/projects/:id - Get project by ID
  getProjectById: async (id: string): Promise<Project> => {
    const response = await api.get(`/projects/${id}`);
    return response.data;
  },
  
  // POST /api/projects/upload-spec - Upload project specification file
  uploadProjectSpec: async (file: File): Promise<string> => {
    const formData = new FormData();
    formData.append('specFile', file);
    const response = await api.post('/projects/upload-spec', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data.url; // Cloudinary URL
  },

  // GET /api/projects/user/:userId - Get projects by user
  getProjectsByUser: async (userId: string): Promise<Project[]> => {
    const response = await api.get(`/projects/user/${userId}`);
    return response.data;
  },

  // GET /api/projects/client/:clientId - Get projects by client
  getProjectsByClient: async (clientId: string): Promise<Project[]> => {
    const response = await api.get(`/projects/client/${clientId}`);
    return response.data;
  },

  // POST /api/projects - Create new project
  createProject: async (data: CreateProjectData): Promise<Project> => {
    const payload = { ...data };
    if (!payload.client?.trim()) {
      delete payload.client;
    }
    const response = await api.post('/projects', payload);
    return response.data;
  },

  // PUT /api/projects/:id - Update project
  updateProject: async (id: string, data: UpdateProjectData): Promise<Project> => {
    const response = await api.put(`/projects/${id}`, data);
    return response.data;
  },

  // DELETE /api/projects/:id - Delete project
  deleteProject: async (id: string): Promise<{ message: string }> => {
    const response = await api.delete(`/projects/${id}`);
    return response.data;
  },
  
  // POST /api/projects/:projectId/tasks/:taskId - Add task to project
  addTaskToProject: async (projectId: string, taskId: string): Promise<Project> => {
    const response = await api.post(`/projects/${projectId}/tasks/${taskId}`);
    return response.data;
  },

  // DELETE /api/projects/:projectId/tasks/:taskId - Remove task from project
  removeTaskFromProject: async (projectId: string, taskId: string): Promise<Project> => {
    const response = await api.delete(`/projects/${projectId}/tasks/${taskId}`);
    return response.data;
  },

  // PATCH /api/projects/:id/archive - Archive/unarchive a project
  archiveProject: async (id: string, archived: boolean): Promise<Project> => {
    const response = await api.patch(`/projects/${id}/archive`, { archived });
    return response.data;
  },
};
